import React, { lazy, Suspense, useEffect } from 'react';
import Paths from './AppPaths';
import Modals from './App.modals';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Route, Redirect } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import BrandContext from '@shoflo/polymer/contexts/BrandContext';
import useLaunchDarkly from '@shoflo/polymer/hooks/useLaunchDarkly/useLaunchDarkly';
import { getActiveSourceForUser } from '@shoflo/polymer/utils/broadcastSourceHelper/broadcastSourceHelper';
import { MetricsProvider } from '@studio/metrics/client/MetricsProvider';
import { LoadReadinessCheckerSLIObserver } from '@studio/metrics/components/LoadReadinessCheckerSLIObserver';
import { ATTRIBUTE_PERSONA_TYPE_HOST } from '@studio/metrics/constants/PersonaType';
import { getEnvironmentType } from '@studio/constants/EnvironmentTypes';
import { broadcastTypesMap } from '@studio/constants/BroadcastTypes';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { datadogRum } from '@datadog/browser-rum';
import getCurrentBroadcast from '@shoflo/studio-redux/actions/broadcast/getCurrentBroadcast';
import getSources from '@shoflo/studio-redux/actions/source/getSources';
import { useLDPayload } from '@shoflo/studio-redux/hooks/useLDPayload/useLDPayload';
import { useRequireAuth } from '@shoflo/studio-redux/hooks/useRequireAuth/useRequireAuth';
import getTeam from '@shoflo/studio-redux/actions/team/getTeam';
import getEvent from '@shoflo/studio-redux/actions/event/getEvent';
import { getBroadcastRolesWithUsers } from '@shoflo/studio-redux/actions/broadcastRoles/getBroadcastRolesWithUsers';
import { useTokenValidationOnFocus } from '@shoflo/studio-redux/utils/useTokenValidationOnFocus';
import { getEnvVar } from '@studio/environment/getEnvVar';
import { Loading } from '@shoflo/polymer/components/Loader/Loader';
import { getURLPathParams } from '@studio/logger/utils/getURLPathParams';
import packageJSON from '../package.json';
import { selectIsCloudProxy } from '@shoflo/studio-redux/selectors/readinessCheckSelector';

const { version } = packageJSON;

// Routes whose entry starts the "load readiness checker" page-load SLI.
const LOAD_READINESS_SLI_PATHS = [Paths.JoinPage];

const AgoraBroadcastPage = lazy(() =>
  import('./containers/pages/Broadcast/AgoraBroadcast')
);
const AgoraJoin = lazy(() => import('./containers/Join/Agora/AgoraJoin'));

const ScenePreviewV2 = lazy(() =>
  import('containers/pages/ScenePreview/ScenePreviewV2')
);

const App = () => {
  const user = useSelector(state => state.user, shallowEqual);
  const broadcast = useSelector(state => state.broadcast, shallowEqual);
  const brandingSettings = useSelector(
    state => state.brandingSettings,
    shallowEqual
  );
  const accountMappingId = useSelector(state => state.team?.externalId);
  const externalEventId = useSelector(state => state.event?.externalId);
  const eventTitle = useSelector(state => state.event?.title);
  const eventStartDate = useSelector(state => state.event?.startOn);
  const eventEndDate = useSelector(state => state.event?.endOn);

  const userId = user?._id;
  const externalUserId = user?.externalId;

  const sourceId = useSelector(state => {
    const broadcastSources = state.broadcastSources;
    const activeSource = broadcastSources;
    getActiveSourceForUser(broadcastSources, userId);
    return activeSource?._id;
  });

  const hasAttendeeCheckedReadiness = useSelector(
    state => state.attendeeReadinessCheck?.hasAttendeeCheckedReadiness,
    shallowEqual
  );

  const isCloudProxy = useSelector(selectIsCloudProxy);

  useRequireAuth();

  const dispatch = useDispatch();

  // Check if all data required for LaunchDarkly is loaded
  // usLDPayload needs: user, team, event, and broadcast
  const teamIdExists = useSelector(state => state.team?._id);
  const eventIdExists = useSelector(state => state.event?._id);
  const broadcastIdExists = useSelector(state => state.broadcast?._id);

  const isRequiredDataLoaded =
    !!teamIdExists && !!eventIdExists && !!broadcastIdExists;

  useEffect(() => {
    dispatch(getTeam());
    dispatch(getEvent());
    dispatch(getCurrentBroadcast());
    dispatch(getSources());
    dispatch(getBroadcastRolesWithUsers());
  }, [dispatch]);

  const pathParams = getURLPathParams();

  const teamId = pathParams?.teamId;
  const eventId = pathParams?.eventId;
  const broadcastId = pathParams?.broadcastId;

  const joinRoute = `/studio-director/teams/${teamId}/events/${eventId}/broadcasts/${broadcastId}/join`;

  const ldKey = getEnvVar('REACT_APP_LAUNCHDARKLY_KEY');
  const ldPayload = useLDPayload();

  // Only initialize LaunchDarkly when we have complete data
  const shouldInitializeLD = ldPayload && isRequiredDataLoaded;
  const isLDInitializedOrTimedOut = useLaunchDarkly(
    shouldInitializeLD ? ldPayload : undefined,
    ldKey
  );
  if (!isRequiredDataLoaded || !ldPayload) return <></>;
  if (shouldInitializeLD && !isLDInitializedOrTimedOut) return <></>;

  const BroadcastPage = AgoraBroadcastPage;
  const Join = AgoraJoin;

  const metricProps = {
    accountMappingId,
    attendeeId: externalUserId,
    studioUserId: userId,
    persona: ATTRIBUTE_PERSONA_TYPE_HOST,
    rudderstackWriteKey: getEnvVar('REACT_APP_RUDDERSTACK_WRITE_KEY'),
    rudderstackDataPlaneUrl: getEnvVar('REACT_APP_RUDDERSTACK_DATA_PLANE_URL'),
    environmentName: getEnvironmentType(getEnvVar('REACT_APP_CVENT_ENV')),
    appVersion: version,
    meetingType: VENDOR_AGORA,
    broadcastId: broadcast?._id,
    teamId: broadcast?.team,
    studioEventId: broadcast?.event,
    sourceId,
    sessionId: broadcast?.externalId,
    sessionStartDate: new Date(broadcast?.startOn),
    sessionEndDate: new Date(broadcast?.endOn),
    sessionName: broadcast?.title,
    broadcastType: broadcastTypesMap.get(
      broadcast?.meta?.broadcastType?.toLowerCase()
    ),
    eventId: externalEventId,
    eventName: eventTitle,
    eventStartDate: new Date(eventStartDate),
    eventEndDate: new Date(eventEndDate),
    isCloudProxy
  };

  return (
    <>
      <AppEffects />
      <MetricsProvider {...metricProps}>
        <LoadReadinessCheckerSLIObserver paths={LOAD_READINESS_SLI_PATHS} />
        <BrandContext.Provider value={brandingSettings}>
          <Modals />
          <Suspense fallback={<Loading />}>
            <Route exact path={Paths.BroadcastPage}>
              {hasAttendeeCheckedReadiness ? (
                <BroadcastPage />
              ) : (
                <Redirect to={joinRoute} />
              )}
            </Route>
            <Route exact path={Paths.ScenePreview} component={ScenePreviewV2} />
            <Route exact path={Paths.JoinPage} component={Join} />
          </Suspense>
        </BrandContext.Provider>
      </MetricsProvider>
    </>
  );
};

/**
 * Encapsulate Application level side effects as to not cause the rest of the
 * app to re-render due to useEffects and redux selectors
 */
function AppEffects() {
  const userId = useSelector(state => state.user?._id);
  const externalUserId = useSelector(state => state.user?.externalId);
  const accountMappingId = useSelector(state => state.team?.externalId);

  useEffect(() => {
    if (userId) {
      datadogRum.setUser({
        id: userId
      });

      datadogRum.setGlobalContextProperty('studio.user.id', userId);
      datadogLogs.setGlobalContextProperty('studio.user.id', userId);
    }
  }, [userId]);

  useEffect(() => {
    if (externalUserId) {
      datadogRum.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
      datadogLogs.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
    }
  }, [externalUserId]);

  useEffect(() => {
    if (accountMappingId) {
      datadogRum.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
      datadogLogs.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
    }
  }, [accountMappingId]);

  /**
   *  Event listener for tab focus: We use this to confirm the user is still
   * authenticated and authorised when the tab becomes focused
   */
  useTokenValidationOnFocus(userId != null);

  return null;
}

export default App;
